import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import { validateGSTNumber, validateEmail } from '../utils/helpers';
import './Settings.css';
import { Settings as SettingsIcon, FileText, Palette, Image as ImageIcon } from 'lucide-react';
import { useAuth, upgradeEvents } from '../context/AuthContext';

const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('general');

  const [formData, setFormData] = useState({
    companyName: '',
    gstNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    stateCode: '',
    phone: '',
    email: '',
    website: '',
    logo: '',
    headerDisplay: 'logo',
    pdfColorMode: 'color',
    currencySymbol: '₹',
    themeColor: '#0793A7',
    financialYearStart: 4,
    defaultTaxType: 'excluded',
    termsAndConditions: '',
    declarationText: '',
    authorizedSignatory: '',
    emailConfig: {
      host: '',
      port: 587,
      secure: false,
      user: '',
      password: '',
      from: ''
    }
  });

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      setFormData(response.data.settings);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('emailConfig.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        emailConfig: {
          ...prev.emailConfig,
          [field]: field === 'port' ? parseInt(value) : field === 'secure' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? parseInt(value) : value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-fill City and State from Pincode
    if (name === 'pincode' && value.length === 6) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${value}`);
        const data = await response.json();
        if (data && data[0].Status === 'Success') {
          const postOffice = data[0].PostOffice[0];
          setFormData(prev => ({
            ...prev,
            city: postOffice.District || postOffice.Block || prev.city,
            state: postOffice.State || prev.state
          }));
        }
      } catch (err) {
        console.error('Failed to fetch pincode details', err);
      }
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setError('Only JPG or PNG images are supported for the PDF logo.');
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        setError('Logo file size must be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          logo: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (formData.gstNumber && !validateGSTNumber(formData.gstNumber)) {
      newErrors.gstNumber = 'Invalid GST number format';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    if (!formData.stateCode) {
      newErrors.stateCode = 'State code is required';
    } else if (!/^[0-9]{2}$/.test(formData.stateCode)) {
      newErrors.stateCode = 'State code must be 2 digits';
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.emailConfig.user && !validateEmail(formData.emailConfig.user)) {
      newErrors['emailConfig.user'] = 'Invalid email format';
    }

    if (formData.emailConfig.from && !validateEmail(formData.emailConfig.from)) {
      newErrors['emailConfig.from'] = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validate()) {
      setError('Please fix the validation errors');
      return;
    }

    try {
      setSaving(true);
      await api.put('/settings', formData);
      setSuccess('Settings updated successfully');
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update settings');
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  const activePreviewColor = formData.pdfColorMode === 'bw' ? '#333333' : (formData.themeColor || '#0793A7');

  return (
    <div style={{ padding: '10px' }}>
      <div className="page-header" style={{ marginBottom: '10px' }}>
        <h1>Settings</h1>
      </div>

      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', borderBottom: '1px solid var(--border)' }}>
        <button type="button" onClick={(e) => { e.preventDefault(); setActiveTab('general'); }} style={{ padding: '10px 15px', background: 'none', border: 'none', borderBottom: activeTab === 'general' ? '2px solid var(--accent)' : '2px solid transparent', color: activeTab === 'general' ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SettingsIcon size={18} /> Business Settings
        </button>
        <button type="button" onClick={(e) => { e.preventDefault(); setActiveTab('pdf'); }} style={{ padding: '10px 15px', background: 'none', border: 'none', borderBottom: activeTab === 'pdf' ? '2px solid var(--accent)' : '2px solid transparent', color: activeTab === 'pdf' ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileText size={18} /> Invoice & PDF Customization
        </button>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <form onSubmit={handleSubmit}>
        {/* Business Settings Tab */}
        <div style={{ display: activeTab === 'general' ? 'block' : 'none' }}>

          {/* Company Information */}
          <div className="card">
            <div className="card-header">Company Information</div>


            <div className="form-group" style={{ padding: '10px' }}>
              <label className="form-label" >Company Name *</label>
              <input
                type="text"
                name="companyName"
                className="form-control"
                value={formData.companyName}
                onChange={handleChange}
                required
              />
              {errors.companyName && <span style={{ color: 'red', fontSize: '12px' }}>{errors.companyName}</span>}
            </div>

            <div className="form-row">
              <div className="form-group" style={{ padding: '10px' }}>
                <label className="form-label">GST Number</label>
                <input
                  type="text"
                  name="gstNumber"
                  className="form-control"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  maxLength="15"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.gstNumber && <span style={{ color: 'red', fontSize: '12px' }}>{errors.gstNumber}</span>}
              </div>

              <div className="form-group" style={{ padding: '10px' }}>
                <label className="form-label">Phone</label>
                <input
                  type="text"
                  name="phone"
                  className="form-control"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && <span style={{ color: 'red', fontSize: '12px' }}>{errors.email}</span>}
              </div>
            </div>

            <div className="form-group" style={{ padding: '10px' }}>
              <label className="form-label">Address</label>
              <textarea
                name="address"
                className="form-control"
                value={formData.address}
                onChange={handleChange}
                rows="2"
              />
            </div>

            <div className="form-row" style={{ padding: '10px' }}>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  name="city"
                  className="form-control"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">State *</label>
                <input
                  type="text"
                  name="state"
                  className="form-control"
                  value={formData.state}
                  onChange={handleChange}
                  required
                />
                {errors.state && <span style={{ color: 'red', fontSize: '12px' }}>{errors.state}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  className="form-control"
                  value={formData.pincode}
                  onChange={handleChange}
                  maxLength="6"
                />
              </div>

              <div className="form-group">
                <label className="form-label">State Code *</label>
                <input
                  type="text"
                  name="stateCode"
                  className="form-control"
                  value={formData.stateCode}
                  onChange={handleChange}
                  maxLength="2"
                  placeholder="e.g., 27 for Maharashtra"
                  required
                />
                {errors.stateCode && <span style={{ color: 'red', fontSize: '12px' }}>{errors.stateCode}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ padding: '10px' }}>
                <label className="form-label">Website</label>
                <input
                  type="text"
                  name="website"
                  className="form-control"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Currency Symbol</label>
                <input
                  type="text"
                  name="currencySymbol"
                  className="form-control"
                  value={formData.currencySymbol}
                  onChange={handleChange}
                  maxLength="3"
                />
              </div>



              <div className="form-group">
                <label className="form-label">Financial Year Start Month</label>
                <select
                  name="financialYearStart"
                  className="form-control"
                  value={formData.financialYearStart}
                  onChange={handleChange}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="card">
            <div className="card-header">Invoice Settings</div>

            <div className="form-group" style={{ padding: '10px' }}>
              <label className="form-label">Default Tax Calculation</label>
              <select
                name="defaultTaxType"
                className="form-control"
                value={formData.defaultTaxType || 'excluded'}
                onChange={handleChange}
              >
                <option value="excluded">Tax Exclusive (Added on top of price)</option>
                <option value="included">Tax Inclusive (Included in price)</option>
              </select>
            </div>

            <div className="form-group" style={{ padding: '10px' }}>
              <label className="form-label" >Terms & Conditions</label>
              <textarea
                name="termsAndConditions"
                className="form-control"
                value={formData.termsAndConditions}
                onChange={handleChange}
                rows="5"
                placeholder="Enter default terms and conditions for invoices"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Declaration Text</label>
              <textarea
                name="declarationText"
                className="form-control"
                value={formData.declarationText}
                onChange={handleChange}
                rows="3"
                placeholder="Declaration statement for invoices"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Authorized Signatory</label>
              <input
                type="text"
                name="authorizedSignatory"
                className="form-control"
                value={formData.authorizedSignatory}
                onChange={handleChange}
                placeholder="Name/designation of authorized signatory"
              />
            </div>
          </div>

          {/* Email Configuration */}
          <div className="card">
            <div className="card-header">Email Configuration (SMTP)</div>
            <p style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '15px' }}>
              Configure SMTP settings to send invoice emails. For Gmail, use app-specific passwords.
            </p>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">SMTP Host</label>
                <input
                  type="text"
                  name="emailConfig.host"
                  className="form-control"
                  value={formData.emailConfig?.host || ''}
                  onChange={handleChange}
                  placeholder="smtp.gmail.com"
                />
              </div>

              <div className="form-group">
                <label className="form-label">SMTP Port</label>
                <input
                  type="number"
                  name="emailConfig.port"
                  className="form-control"
                  value={formData.emailConfig?.port || 587}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="emailConfig.secure"
                    checked={formData.emailConfig?.secure || false}
                    onChange={handleChange}
                  />
                  Use SSL/TLS
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email Username</label>
                <input
                  type="email"
                  name="emailConfig.user"
                  className="form-control"
                  value={formData.emailConfig?.user || ''}
                  onChange={handleChange}
                  placeholder="your-email@gmail.com"
                />
                {errors['emailConfig.user'] && <span style={{ color: 'red', fontSize: '12px' }}>{errors['emailConfig.user']}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Email Password</label>
                <input
                  type="password"
                  name="emailConfig.password"
                  className="form-control"
                  value={formData.emailConfig?.password || ''}
                  onChange={handleChange}
                  placeholder="App-specific password"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">From Email Address</label>
              <input
                type="email"
                name="emailConfig.from"
                className="form-control"
                value={formData.emailConfig?.from || ''}
                onChange={handleChange}
                placeholder="noreply@yourcompany.com"
              />
              {errors['emailConfig.from'] && <span style={{ color: 'red', fontSize: '12px' }}>{errors['emailConfig.from']}</span>}
            </div>
          </div>
        </div>

        {/* PDF Customization Tab */}
        <div style={{ display: activeTab === 'pdf' ? 'block' : 'none' }}>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Left Column: Controls */}
            <div style={{ flex: '1 1 350px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

              <div className="card">
                <div className="card-header"><ImageIcon size={18} style={{ marginRight: '8px' }} /> Invoice Logo</div>
                <div style={{ padding: '15px' }}>
                  {user?.planType === 'starter' ? (
                    <div style={{ textAlign: 'center', padding: '20px 10px' }}>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>Custom branding is available on the Professional plan.</p>
                      <button onClick={() => upgradeEvents.emit({ feature: 'customBranding', requiredPlan: 'professional', message: 'Custom branding is available on the Professional plan.' })} className="btn btn-primary btn-small" style={{ cursor: 'pointer' }}>Upgrade to Professional</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      {formData.logo ? (
                        <div style={{ position: 'relative' }}>
                          <img src={formData.logo} alt="Company Logo" style={{ height: '80px', maxWidth: '200px', objectFit: 'contain', border: '1px solid var(--border)', borderRadius: '4px', padding: '5px', background: '#fff' }} />
                          <button type="button" onClick={handleRemoveLogo} style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: '1' }}>&times;</button>
                        </div>
                      ) : (
                        <div style={{ height: '80px', width: '150px', border: '2px dashed var(--border)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>No Logo</div>
                      )}
                      <div>
                        <input type="file" accept="image/jpeg, image/png" onChange={handleLogoUpload} id="pdf-logo-upload" style={{ display: 'none' }} />
                        <label htmlFor="pdf-logo-upload" className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-block', margin: 0 }}>Upload Logo</label>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>Required: PNG or JPG. Max size: 2MB.</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header"><FileText size={18} style={{ marginRight: '8px' }} /> Header Display</div>
                <div style={{ padding: '15px' }}>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '15px' }}>Choose what to display at the top of your PDF invoices.</p>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                      <input
                        type="radio"
                        name="headerDisplay"
                        value="logo"
                        checked={formData.headerDisplay === 'logo' || !formData.headerDisplay}
                        onChange={handleChange}
                        style={{ cursor: 'pointer' }}
                      />
                      Company Logo
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                      <input
                        type="radio"
                        name="headerDisplay"
                        value="name"
                        checked={formData.headerDisplay === 'name'}
                        onChange={handleChange}
                        style={{ cursor: 'pointer' }}
                      />
                      Company Name (Text)
                    </label>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><Palette size={18} style={{ marginRight: '8px' }} /> Theme Color</div>
                <div style={{ padding: '15px' }}>
                  {user?.planType === 'starter' ? (
                    <div style={{ textAlign: 'center', padding: '20px 10px' }}>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>Custom theme colors are available on the Professional plan.</p>
                      <button onClick={() => upgradeEvents.emit({ feature: 'customBranding', requiredPlan: 'professional', message: 'Custom theme colors are available on the Professional plan.' })} className="btn btn-primary btn-small" style={{ cursor: 'pointer' }}>Upgrade to Professional</button>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '15px' }}>This color will be used for your invoice headers, totals, and overall app theme.</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="color"
                          name="themeColor"
                          className="form-control"
                          value={formData.themeColor}
                          onChange={handleChange}
                          style={{ width: '80px', padding: '2px', height: '40px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 'bold' }}>{formData.themeColor}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header"><Palette size={18} style={{ marginRight: '8px' }} /> PDF Color Mode</div>
                <div style={{ padding: '15px' }}>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '15px' }}>Choose whether to generate Color or Black & White PDFs.</p>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                      <input
                        type="radio"
                        name="pdfColorMode"
                        value="color"
                        checked={formData.pdfColorMode === 'color' || !formData.pdfColorMode}
                        onChange={handleChange}
                        style={{ cursor: 'pointer' }}
                      />
                      Color
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                      <input
                        type="radio"
                        name="pdfColorMode"
                        value="bw"
                        checked={formData.pdfColorMode === 'bw'}
                        onChange={handleChange}
                        style={{ cursor: 'pointer' }}
                      />
                      Black & White
                    </label>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Preview */}
            <div style={{ flex: '1 1 400px' }}>
              <div className="card" style={{ background: '#f8fafc', padding: '20px', border: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '1px' }}>Live PDF Preview</h3>

                {/* Mock Invoice Paper */}
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', padding: '20px', width: '100%', minHeight: '350px' }}>
                  {/* Header Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #f1f5f9', paddingBottom: '15px', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '60%' }}>
                      {(formData.headerDisplay !== 'name' && formData.logo) ? (
                        <img src={formData.logo} alt="Logo Preview" style={{ height: '40px', objectFit: 'contain', marginBottom: '10px', objectPosition: 'left' }} />
                      ) : (
                        <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 5px 0', color: activePreviewColor }}>{formData.companyName || 'FASTINVOICE'}</h2>
                      )}
                      <span style={{ fontSize: '9px', color: '#64748b', lineHeight: '1.4' }}>
                        {formData.address || 'H-WING 250 LADAK BANGLOWS'}<br />
                        {formData.city || 'MUMBAI'}, {formData.state || 'Maharashtra'} - {formData.pincode || '380010'}<br />
                        GSTIN: {formData.gstNumber || '27XXXXX0000X1Z5'}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 5px 0', color: '#0f172a' }}>TAX INVOICE</h2>
                      <span style={{ fontSize: '10px', color: '#64748b' }}>(Original for Recipient)</span>
                    </div>
                  </div>

                  {/* Mock Table */}
                  <div style={{ width: '100%', borderBottom: '1px solid #e2e8f0', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', background: 'color-mix(in srgb, ' + activePreviewColor + ' 10%, transparent)', padding: '8px', fontSize: '10px', fontWeight: 'bold', color: activePreviewColor }}>
                      <div style={{ flex: '0.5' }}>Sr.</div>
                      <div style={{ flex: '2' }}>Item Description</div>
                      <div style={{ flex: '1', textAlign: 'right' }}>Amount</div>
                    </div>
                    <div style={{ display: 'flex', padding: '8px', fontSize: '10px', color: '#334155' }}>
                      <div style={{ flex: '0.5' }}>1</div>
                      <div style={{ flex: '2', fontWeight: 'bold' }}>HOSTING<br /><span style={{ fontWeight: 'normal', color: '#64748b', fontSize: '9px' }}>1 Year Plan</span></div>
                      <div style={{ flex: '1', textAlign: 'right' }}>{formData.currencySymbol} 1953.12</div>
                    </div>
                  </div>

                  {/* Mock Totals */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ width: '50%', fontSize: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px' }}>
                        <span style={{ color: '#64748b' }}>Subtotal:</span>
                        <span>{formData.currencySymbol} 1953.12</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px' }}>
                        <span style={{ color: '#64748b' }}>IGST (28%):</span>
                        <span>{formData.currencySymbol} 546.88</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: activePreviewColor, color: 'white', fontWeight: 'bold', marginTop: '5px', borderRadius: '4px' }}>
                        <span>GRAND TOTAL:</span>
                        <span>{formData.currencySymbol} 2500.00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
